import { apiCall, GET } from '../../../assets/scripts/interfaces/api-call'
import Component, { loadComponent } from '../../../assets/scripts/modules/component.js'

const MOBILE_BREAKPOINT = 800
const DEBOUNCE_TIME = 300 // in ms; change to 0 for instant results
const SEARCH_INPUT_OFFSET = 12 // margin between the input and the top of the screen
class SearchBarComponent extends Component {
  init() {
    this.suggestionEndpoint = this.element.getAttribute('data-autosuggest-endpoint-url')
    this.inputContainer = this.element.querySelector('.search-bar__input-container')
    this.form = this.element.querySelector('.search-bar__form')
    this.input = this.element.querySelector('.search-bar__field-input')
    this.datalist = this.element.querySelector('.search-bar__datalist')
    this.submitButton = this.element.querySelector('.button--search')
    this.clearButton = this.element.querySelector('.button--clear')

    this.debounceTimer = null
    this.currentIndex = -1
    this.suggestions = []
    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT
    this.connected = false

    if (!this.input || !this.suggestionEndpoint || !this.datalist) return

    this.boundMethods = {
      handleInput: this.handleInput.bind(this),
      handleKeyDown: this.handleKeyDown.bind(this),
      handleInputFocus: this.handleInputFocus.bind(this),
      handleClickOutside: this.handleClickOutside.bind(this),
      fetchResults: this.fetchResults.bind(this),
      clearInput: this.clearInput.bind(this),
      handleFocusOut: this.handleFocusOut.bind(this),
    }

    this.connect()
  }

  connect() {
    if (this.connected) return
    this.connected = true

    this.input.addEventListener('input', this.boundMethods.handleInput)
    this.input.addEventListener('keydown', this.boundMethods.handleKeyDown)
    this.input.addEventListener('focus', this.boundMethods.handleInputFocus)
    this.element.addEventListener('focusout', this.boundMethods.handleFocusOut)
    document.addEventListener('click', this.boundMethods.handleClickOutside)
    this.submitButton.addEventListener('click', this.boundMethods.fetchResults)
    this.clearButton.addEventListener('click', this.boundMethods.clearInput)

    if (this.input.id) {
      const params = new URL(document.location.toString()).searchParams
      const value = params.get(this.input.id)

      if (value) {
        this.input.value = value
        this.clearButton.classList.remove('button--hidden')
      }

      this.input.blur()
    }

    //if (!this.isMobile) this.input.focus()
  }

  disconnect() {
    if (!this.connected) return
    this.connected = false

    this.input.removeEventListener('input', this.boundMethods.handleInput)
    this.input.removeEventListener('keydown', this.boundMethods.handleKeyDown)
    this.input.removeEventListener('focus', this.boundMethods.handleInputFocus)
    this.element.removeEventListener('focusout', this.boundMethods.handleFocusOut)
    document.removeEventListener('click', this.boundMethods.handleClickOutside)
    this.submitButton.removeEventListener('click', this.boundMethods.fetchResults)
    this.clearButton.removeEventListener('click', this.boundMethods.clearInput)

    clearTimeout(this.debounceTimer)
  }

  handleInput() {
    const query = this.input.value.trim()

    if (query.length > 0) {
      this.clearButton.classList.remove('button--hidden')
    } else {
      this.clearButton.classList.add('button--hidden')
    }

    if (query.length < 2) {
      this.datalist.innerHTML = ''
      this.closeSuggestions()
      return
    }

    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchSuggestions(query), DEBOUNCE_TIME)
  }

  clearInput() {
    this.input.value = ''
    this.closeSuggestions()
    this.clearButton.classList.add('button--hidden')
    this.datalist.innerHTML = ''
    this.input.focus()
  }

  handleFocusOut(e) {
    const relatedTarget = e.relatedTarget
    if (!this.element.contains(relatedTarget)) {
      this.closeSuggestions()
    }
  }

  async fetchSuggestions(query) {
    const url = this.suggestionEndpoint.replace('QUERY', query)
    const { items } = await apiCall(GET, url).catch(error => console.warn(error))
    this.suggestions = items || []

    this.updateDatalist()
  }

  updateDatalist() {
    if (!this.suggestions || this.suggestions.length === 0) {
      this.closeSuggestions()
      return
    }

    this.datalist.innerHTML = ''
    this.currentIndex = -1

    this.suggestions.forEach(suggestion => {
      const li = document.createElement('li')
      li.textContent = suggestion.name
      li.classList.add('search-bar__option')
      li.addEventListener('click', () => this.selectSuggestion(suggestion))
      li.innerHTML = this.highlightQueryString(suggestion.name, this.input.value)

      this.datalist.appendChild(li)
    })

    this.input.setAttribute('aria-expanded', 'true')
    this.datalist.style.display = 'block'
    this.form.classList.add('search-bar__form--expanded')
  }

  highlightQueryString(text, query) {
    const regex = new RegExp(query, 'gi')
    return text.replace(regex, `<span class="search-bar__option--highlighted">${query}</span>`)
  }

  handleKeyDown(e) {
    if (this.suggestions.length === 0) return

    if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault()

      if (this.datalist.style.display === 'none' && this.input.value.length > 1) {
        this.datalist.style.display = 'block'
        this.form.classList.add('search-bar__form--expanded')
        this.input.setAttribute('aria-expanded', 'true')

        if (e.key === 'ArrowDown' && !e.altKey) {
          this.currentIndex = 0
        } else if (e.key === 'ArrowUp') {
          this.currentIndex = this.suggestions.length - 1
        }
      } else {
        this.currentIndex = (this.currentIndex + (e.key === 'ArrowDown' ? 1 : -1) + this.suggestions.length) % this.suggestions.length
      }
      this.updateActiveDescendant()
    } else if (e.key === 'Enter' && this.currentIndex >= 0) {
      e.preventDefault()
      this.selectSuggestion(this.suggestions[this.currentIndex])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      // close suggestions list if it's open; otherwise, clear the input field
      this.datalist.style.display === 'block' ? this.closeSuggestions() : (this.input.value = '')
    } else if (['Home', 'End'].includes(e.key)) {
      e.preventDefault()
      this.input.setSelectionRange(e.key === 'Home' ? 0 : this.input.value.length, this.input.value.length)
    }
  }

  updateActiveDescendant() {
    const options = this.datalist.querySelectorAll('.search-bar__option')
    if (!options || !options.length) return false

    this.removeActiveOnOption(options)
    if (this.currentIndex >= options.length) this.currentIndex = 0
    if (this.currentIndex < 0) this.currentIndex = options.length - 1
    options[this.currentIndex].classList.add('search-bar__option--active')
    options[this.currentIndex].setAttribute('aria-selected', 'true')

    if (this.currentIndex >= 0) {
      this.input.setAttribute('aria-activedescendant', options[this.currentIndex].id)
    } else {
      this.input.removeAttribute('aria-activedescendant')
    }

    const selectedElement = options[this.currentIndex]
    selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  removeActiveOnOption(options) {
    for (let i = 0; i < options.length; i++) {
      options[i].classList.remove('search-bar__option--active')
      options[i].removeAttribute('aria-selected')
    }
  }

  selectSuggestion(suggestion) {
    this.input.value = suggestion.name
    this.fetchResults()
  }

  closeSuggestions() {
    this.datalist.style.display = 'none'
    this.input.setAttribute('aria-expanded', 'false')
    this.input.removeAttribute('aria-activedescendant')
    this.form.classList.remove('search-bar__form--expanded')
  }

  fetchResults() {
    const query = this.input.value.trim()
    if (query.length < 2) return

    this.form.submit()
  }

  handleInputFocus() {
    if (this.isMobile) {
      const rect = this.input.getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      window.scrollTo({
        top: rect.top + scrollTop - SEARCH_INPUT_OFFSET,
        behavior: 'smooth',
      })
    }
  }

  handleClickOutside(event) {
    if (!this.input.contains(event.target) && !this.datalist.contains(event.target)) {
      this.closeSuggestions()
    }
  }
}

window.addEventListener('init-load', () => loadComponent(document, 'search-bar', SearchBarComponent))

window.addEventListener('beforeunload', () => {
  document.querySelectorAll('[data-component="search-bar"]').forEach(element => {
    element.instance?.disconnect()
  })
})
